<template>
  <div>
    <div
        v-show="isBusy"
        class="absolute inset-0 bg-faint-white flex items-center justify-center z-10"
    >
      <spinner :color="'text-black'" :size="10" />
    </div>

    <div class="flex items-center mb-5">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Equipment Information
      </h3>
      <hr class="flex-1 border-primary mx-4" />
      <button
          @click="updateEquipment"
          class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path
              d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
          />
          <path d="M17 21v-8H7v8M7 3v5h8" />
        </svg>
        <span>Update Details</span>
      </button>
    </div>

    <div
        class="grid grid-cols-4 gap-5"
        style="grid-template-rows: repeat(3, auto);"
    >
      <!-- Row 1 -->
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">SERVICE AGENT</label>
        <ContactAutocomplete
            class="w-full"
            v-model="local_equipment.service_agent"
            :forceSelection="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">SELLER AGENT</label>
        <ContactAutocomplete
            class="w-full"
            v-model="local_equipment.seller_agent"
            :forceSelection="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">OWNER</label>
        <ContactAutocomplete
            class="w-full"
            v-model="local_equipment.owner"
            :forceSelection="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">SOLD CONDITION</label>
        <Dropdown
            class="w-full bge-input rounded"
            v-model="local_equipment.sold_condition"
            :options="soldConditionList"
            optionLabel="name"
            optionValue="id"
            placeholder="<Sold Condition>"
            :showClear="true"
        />
      </div>

      <!-- Row 2 -->
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">MAKE</label>
        <Dropdown
            class="w-full bge-input rounded"
            v-model="local_equipment.make"
            :options="equipmentMakeList"
            optionLabel="name"
            optionValue="id"
            placeholder="<Make>"
            :showClear="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">LOCATION</label>
        <select v-model="local_equipment.location" class="bge-input bge-select rounded">
          <option v-for="loc in equipmentLocations" :key="loc" :value="loc">{{ loc }}</option>
        </select>
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">MODEL</label>
        <input
            v-model="local_equipment.model_name"
            class="bge-input bge-input-spacing rounded"
            type="text"
        />
      </div>
      <div class="flex flex-col  row-span-2">
        <!-- DESCRIPTION spans two rows -->
        <label class="uppercase font-semibold text-sm">DESCRIPTION</label>
        <textarea
            v-model="local_equipment.description"
            class="bge-input bge-input-spacing rounded"
            rows="5"
        ></textarea>
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm flex items-center">
          PRODUCT CODE
          <SearchTipsInfo class="ml-2" />
        </label>
        <ProductAutoComplete
            class="w-full"
            v-model="selectedProduct"
            :showTips="false"
            :forceSelection="true"
            @productSelected="handleProductSelected"
            @productCleared="handleProductCleared"
        />
      </div>

      <!-- Row 3 -->
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">ASSIGNED CALIBRATION INTERVAL</label>
        <input
            v-model="local_equipment.calibration_interval"
            class="bge-input bge-input-spacing rounded"
            type="number"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">ASSIGNED SERVICE INTERVAL</label>
        <input
            v-model="local_equipment.service_interval"
            class="bge-input bge-input-spacing rounded"
            type="number"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">SERIAL NUMBER</label>
        <input
            v-model="local_equipment.serial_number"
            class="bge-input bge-input-spacing rounded"
            type="text"
        />
      </div>

      <!-- Row 4 -->
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">DELIVERY DATE</label>
        <Calendar
            appendTo="body"
            dateFormat="dd/mm/yy"
            v-model="local_equipment.delivery_date"
            :showIcon="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">WARRANTY EXPIRY</label>
        <Calendar
            appendTo="body"
            dateFormat="dd/mm/yy"
            v-model="local_equipment.warranty_expiry"
            :showIcon="true"
        />
      </div>
      <div class="flex flex-col">
        <label class="uppercase font-semibold text-sm">IS LOAN UNIT</label>
        <ToggleButton
            v-model="local_equipment.is_loan_unit"
            onLabel="YES"
            offLabel="NO"
            style="width: 10em"
        />
      </div>
      <div class="flex flex-wrap gap-4">
        <div class="flex flex-col basis-full md:basis-1/2">
          <label class="uppercase font-semibold text-sm">IS OPERATIONAL</label>
          <ToggleButton
              v-model="local_equipment.is_operational"
              onLabel="YES"
              offLabel="NO"
              style="width: 10em"
          />
        </div>

        <div class="flex flex-col basis-full md:basis-1/2">
          <label class="uppercase font-semibold text-sm">MANAGED BY US</label>
          <ToggleButton
              v-model="local_equipment.is_managed"
              onLabel="YES"
              offLabel="NO"
              style="width: 10em"
          />
        </div>
      </div>

    </div>
  </div>
</template>


<script>
import Spinner from "@/components/Spinner.vue";
import ToggleButton from "primevue/togglebutton";
import ContactAutocomplete from "@/components/AutoComplete/ContactAutoComplete.vue";
import notificationMixin from "@/mixins/notificationMixin";
import ProductAutoComplete from "@/components/AutoComplete/ProductAutoComplete.vue";
import SearchTipsInfo from "@/components/AutoComplete/SearchTipsInfo.vue";

export default {
  name: "EquipmentInformation",
  mixins: [notificationMixin],
  components: {
    Spinner,
    ToggleButton,
    ContactAutocomplete,
    ProductAutoComplete,
    SearchTipsInfo,
  },
  props: {
    equipment: {
      type: Object,

    },
  },
  data() {
    return {
      isBusy:false,
      // The search box shows the product code of the linked product
      selectedProduct: this.equipment.product_code
          ? { product_id: this.equipment.product_id, product_code: this.equipment.product_code, name: this.equipment.product_code }
          : "",
      equipmentMakeList: [],
      // Where the equipment is; the same four values the record allows
      equipmentLocations: ["Customer", "Agent", "Workshop", "Stock"],
      soldConditionList: [],
      local_equipment: {
        ...this.equipment,
        service_agent: this.equipment.service_agent
            ? { contact_id: this.equipment.service_agent.contact_id || null, name: this.equipment.service_agent.name || null }
            : null,
        seller_agent: this.equipment.seller_agent
            ? { contact_id: this.equipment.seller_agent.contact_id || null, name: this.equipment.seller_agent.name || null }
            : null,
        owner: this.equipment.owner
            ? { contact_id: this.equipment.owner.contact_id || null, name: this.equipment.owner.name || null }
            : { contact_id: null, name: null },
        delivery_date: this.equipment.delivery_date
            ? this.formatTimestamp(this.equipment.delivery_date)
            : null,
        warranty_expiry: this.equipment.warranty_expiry
            ? this.formatTimestamp(this.equipment.warranty_expiry)
            : null,

      },
    };
  },

  async mounted() {
    this.fetchEquipmentMakes();
    this.fetchSoldConditions();
  },
  watch: {
    // Delivery date set/changed -> warranty expiry follows: delivery date plus
    // the product's warranty period in months (field request). The computed
    // date lands in the visible calendar field and stays editable.
    "local_equipment.delivery_date": function (newDeliveryDate) {
      this.applyWarrantyExpiry(newDeliveryDate);
    },
  },
  methods: {
    // Changing the product corrects a wrong code: code, description and model
    // follow the chosen product (model rule matches adding new equipment).
    handleProductSelected(selectedProduct) {
      const product = selectedProduct && selectedProduct.value ? selectedProduct.value : selectedProduct;
      if (!product || !product.product_id) {
        this.handleProductCleared();
        return;
      }

      const normalizedType = (product.product_type || product.type || "").trim().toUpperCase();
      if (
          normalizedType !== "EQUIPMENT WITH SERIAL NUMBER" &&
          normalizedType !== "EQUIPMENT WITHOUT SERIAL NUMBER"
      ) {
        this.notifyError(
            "Only products with type 'EQUIPMENT WITH SERIAL NUMBER' or 'EQUIPMENT WITHOUT SERIAL NUMBER' can be selected."
        );
        this.handleProductCleared();
        return;
      }

      const productModel = typeof product.model_name === "string" ? product.model_name.trim() : "";

      this.local_equipment.product_id = product.product_id;
      this.local_equipment.product_code = product.product_code;
      this.local_equipment.description = product.description || "";
      this.local_equipment.model_name = productModel || product.product_code;
      this.selectedProduct = { product_id: product.product_id, product_code: product.product_code, name: product.product_code };
    },

    // Cleared or left without picking -> keep the product the equipment already has
    handleProductCleared() {
      this.selectedProduct = this.local_equipment.product_code
          ? { product_id: this.local_equipment.product_id, product_code: this.local_equipment.product_code, name: this.local_equipment.product_code }
          : "";
    },

    async fetchEquipmentMakes() {
      try {
        const response = await this.DatamasterService.getDataMasters(
            "?noPaging=true&type=EquipmentMake&is_active=true&orderBy=order&order=1"
        );

        this.equipmentMakeList = (response || []).map((item) => ({
          id: item.name,
          name: item.name,
        }));

        // Older equipment may hold a free-text make that is not in the master
        // list — keep it selectable so the saved value still displays.
        const currentMake = this.local_equipment ? this.local_equipment.make : "";
        if (currentMake && !this.equipmentMakeList.some((item) => item.id === currentMake)) {
          this.equipmentMakeList.push({ id: currentMake, name: currentMake });
        }
      } catch (error) {
        console.error("Error fetching equipment makes:", error);
        this.equipmentMakeList = [];
      }
    },
    async updateEquipment() {
      try {
        this.isBusy = true;

        // Map back the selected IDs without optional chaining
        this.local_equipment.service_agent_id = this.local_equipment.service_agent && this.local_equipment.service_agent.contact_id
            ? this.local_equipment.service_agent.contact_id
            : null;

        this.local_equipment.seller_agent_id = this.local_equipment.seller_agent && this.local_equipment.seller_agent.contact_id
            ? this.local_equipment.seller_agent.contact_id
            : null;

        this.local_equipment.owner_id = this.local_equipment.owner && this.local_equipment.owner.contact_id
            ? this.local_equipment.owner.contact_id
            : null;

        const updatedEquipment = {
          ...this.local_equipment,
          delivery_date: this.local_equipment.delivery_date
              ? this.$moment(this.local_equipment.delivery_date, 'DD-MM-YYYY').unix()
              : null,
          warranty_expiry: this.local_equipment.warranty_expiry
              ? this.$moment(this.local_equipment.warranty_expiry, 'DD-MM-YYYY').unix()
              : null,
        };

        // Update equipment data
        await this.EquipmentService.updateEquipment(this.local_equipment.equipment_id, updatedEquipment);
        this.$emit("refreshEquipment");
        this.isBusy = false;
        this.notifySuccess("Equipment updated successfully");
      } catch (error) {
        const backendError = error && error.data ? error.data : null;
        const backendMessage = backendError && backendError.message
            ? (Array.isArray(backendError.message) ? backendError.message[0] : backendError.message)
            : null;

        console.error("Error updating equipment:", backendMessage || error);
        this.notifyError(backendMessage || "Couldn't update equipment");
        this.isBusy = false;
      }
    },

    formatTimestamp(unixTimestamp) {
      return this.$moment.unix(unixTimestamp).format('DD-MM-YYYY');
    },

    async fetchSoldConditions() {
      try {
        const response = await this.DatamasterService.getDataMasters(
            "?noPaging=true&type=SoldCondition&is_active=true&orderBy=order&order=1"
        );

        this.soldConditionList = (response || []).map((item) => ({
          id: item.value,
          name: item.name,
        }));

        // Older equipment may hold a value no longer in the master list —
        // keep it selectable so the saved value still displays.
        const current = this.local_equipment ? this.local_equipment.sold_condition : "";
        if (current && !this.soldConditionList.some((item) => item.id === current)) {
          this.soldConditionList.push({ id: current, name: current });
        }
      } catch (error) {
        console.error("Error fetching sold conditions:", error);
        this.soldConditionList = [];
      }
    },

    applyWarrantyExpiry(deliveryDate) {
      if (!deliveryDate) return;

      const warrantyPeriod = this.equipment.product && this.equipment.product.warranty_period
          ? parseInt(this.equipment.product.warranty_period)
          : null;
      if (!warrantyPeriod || isNaN(warrantyPeriod)) return;

      const delivery = deliveryDate instanceof Date
          ? this.$moment(deliveryDate)
          : this.$moment(deliveryDate, 'DD-MM-YYYY');
      if (!delivery.isValid()) return;

      this.local_equipment.warranty_expiry = delivery.add(warrantyPeriod, 'months').format('DD-MM-YYYY');
    },

  },
};
</script>
